/* eslint-disable max-len */
/* eslint-disable max-lines */

import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import SkeletonTicket from "../../components/reservation_v2/SkeletonTicket";
import DiagnosticsPanel from "../../components/esim/diagnostics/DiagnosticsPanel";
import { REFUND_TONE } from "../../components/esim/EsimReservations";
import { IEsimProfile } from "../../interfaces/esim/EsimReservations.types";
import { getEsimProfile } from "../../services/Esim.service";
import { esimProfileRefundView } from "../../utils/esim/esimRefund";
import {
  getEsimProfileStatusColor,
  getEsimProfileStatusDescription,
  getEsimProfileStatusLabel
} from "../../utils/esim/esimStatus";

const EsimProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<IEsimProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (id) {
          const data = await getEsimProfile(parseInt(id));
          setProfile(data);
        }
      } catch (error) {
        console.error("Error fetching ESIM profile:", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchProfile();
  }, [id]);

  if (loading) {
    return <SkeletonTicket loading={loading} />;
  }

  if (!profile) {
    return <div className="text-center text-red-500">Profile not found</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 flex-col w-full items-center">
        <div className="w-full lg:w-[70%]">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Kthehu</span>
            </button>
            <h1 className="text-2xl font-semibold">Detajet e profilit ESIM</h1>
            <div className="ml-auto">
              <DiagnosticsPanel profileId={profile.esimProfileId} />
            </div>
          </div>
          <div className="bg-gray-100 p-6 rounded-lg shadow-sm">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">
                    {profile.clientName}
                  </h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <p className="text-sm text-gray-500">
                      ID: {profile.esimProfileId}
                    </p>
                    <p className="text-sm text-gray-500">
                      ICCID: {profile.iccid}
                    </p>
                    {profile.email && (
                      <p className="text-sm text-gray-500">
                        Email:{" "}
                        <a
                          href={`mailto:${profile.email}`}
                          className="text-primary hover:underline break-all"
                        >
                          {profile.email}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium
                  text-center  ${
                    profile.isActive
                      ? "bg-green text-white"
                      : "bg-red text-white"
                  }`}
                  >
                    {profile.isActive ? "Aktiv" : "Jo aktiv"}
                  </div>
                  {profile.esimProfileStatus && (
                    <>
                      <div
                        className={`px-2 py-0.5 rounded-full mb-1 text-sm text-center cursor-pointer
                      font-medium ${getEsimProfileStatusColor(
                        profile.esimProfileStatus
                      )}`}
                        data-tooltip-content={getEsimProfileStatusDescription(
                          profile.esimProfileStatus
                        )}
                        data-tooltip-id="statusDescription"
                      >
                        🛜 {getEsimProfileStatusLabel(profile.esimProfileStatus)}
                      </div>
                      <Tooltip delayShow={2000} id="statusDescription" className="z-50" />
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Total i blerë</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalPurchasedEuro.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Total i shpenzuar</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalSpentEuro.toFixed(2)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-sm text-gray-600">Zbritje totale</h3>
                  <p className="text-xl font-semibold">
                    €{profile.totalDiscountEuro.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="text-sm text-gray-600 mb-2">
                  Të dhënat e përdorura
                </h3>
                <p className="text-xl font-semibold">
                  {(profile.totalUsedDataBytes / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Rezervimet</h3>
                <div className="space-y-4">
                  {profile.reservations.map((reservation) => (
                    <div
                      key={reservation.reservationId}
                      className="border-b pb-4 last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {reservation.packageName}
                            </p>
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                              ID: {reservation.reservationId}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {new Date(reservation.insertDate).toLocaleString(
                              "sq-AL",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                              }
                            )}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <p className="font-semibold">
                            €{reservation.paidAmount.toFixed(2)}
                          </p>
                          {(() => {
                            const refund = esimProfileRefundView(reservation);
                            return refund && (
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${REFUND_TONE[refund.tone]}`}
                              >
                                {refund.label}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsimProfile;
